import { toDateKey } from './dateTZ';

/**
 * Centralized timeline calculation utilities
 * All date calculations use timezone-aware helpers to prevent 1-day shifts
 */

/**
 * Calculate duration in days (inclusive of both start and end dates)
 * Timezone-aware calculation
 * 
 * @param startDate - Start date string (YYYY-MM-DD or ISO)
 * @param endDate - End date string (YYYY-MM-DD or ISO)
 * @returns Duration in days (minimum 1)
 * 
 * @example
 * calculateDuration('2026-01-01', '2026-01-10') // 10 days
 * calculateDuration('2026-01-15', '2026-01-15') // 1 day (single-day)
 */
export function calculateDuration(startDate: string, endDate: string): number {
  const startKey = toDateKey(startDate);
  const endKey = toDateKey(endDate);
  
  const start = new Date(startKey + 'T00:00:00');
  const end = new Date(endKey + 'T00:00:00');
  
  const durationInMs = end.getTime() - start.getTime();
  return Math.max(1, Math.floor(durationInMs / (1000 * 60 * 60 * 24)) + 1);
}

/**
 * Compare two date strings in timezone-aware manner
 * 
 * @param a - First date string
 * @param b - Second date string
 * @returns -1 if a < b, 0 if a === b, 1 if a > b
 * 
 * @example
 * compareDates('2026-01-01', '2026-01-10') // -1
 * compareDates('2026-01-15', '2026-01-15') // 0
 * compareDates('2026-02-01', '2026-01-01') // 1
 */
export function compareDates(a: string, b: string): number {
  const aKey = toDateKey(a);
  const bKey = toDateKey(b);
  
  if (aKey < bKey) return -1;
  if (aKey > bKey) return 1;
  return 0;
}

/**
 * Format date for display in Vietnamese locale
 * 
 * @param dateStr - Date string (YYYY-MM-DD or ISO)
 * @param format - Format type: 'full' (dd/mm/yyyy) or 'short' (dd/mm/yy)
 * @returns Formatted date string
 * 
 * @example
 * formatDateDisplay('2026-01-10') // '10/01/2026'
 * formatDateDisplay('2026-01-10', 'short') // '10/01/26'
 */
export function formatDateDisplay(dateStr: string, format: 'full' | 'short' = 'full'): string {
  try {
    const dateKey = toDateKey(dateStr);
    const date = new Date(dateKey + 'T00:00:00');
    
    return date.toLocaleDateString('vi-VN', { 
      day: '2-digit', 
      month: '2-digit',
      year: format === 'short' ? '2-digit' : 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error, dateStr);
    return '-';
  }
}

/**
 * Get min/max dates from an array of date strings
 * 
 * @param dates - Array of date strings
 * @returns Object with start and end dates, or null if empty array
 * 
 * @example
 * getDateRange(['2026-01-10', '2026-01-01', '2026-01-15']) 
 * // { start: '2026-01-01', end: '2026-01-15' }
 */
export function getDateRange(dates: string[]): { start: string; end: string } | null {
  if (dates.length === 0) return null;
  
  const dateKeys = dates.map(d => toDateKey(d));
  const sorted = dateKeys.sort();
  
  return {
    start: sorted[0],
    end: sorted[sorted.length - 1]
  };
}
